/**
 * Current Stock Hooks
 * @see US-12b - Stock Actuel (Consultation)
 * @see Sprint 27 - Stock a Date
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  getCurrentStock,
  getStockKPIs,
  getStockBreakdown,
} from '../services/current-stock.service';
import type { QueryCurrentStockDto, QueryStockBreakdownDto } from '@bstock/shared';

/**
 * Query key factory for current stock
 */
export const currentStockKeys = {
  all: ['current-stock'] as const,
  list: (params: QueryCurrentStockDto) =>
    [...currentStockKeys.all, 'list', params] as const,
  kpis: () => [...currentStockKeys.all, 'kpis'] as const,
  breakdown: (id: string, params?: QueryStockBreakdownDto) =>
    [...currentStockKeys.all, 'breakdown', id, params] as const,
};

/**
 * Hook to get current stock list with pagination and filters
 * Supports optional date param for historical stock
 */
export function useCurrentStock(params: QueryCurrentStockDto = {}) {
  return useQuery({
    queryKey: currentStockKeys.list(params),
    queryFn: () => getCurrentStock(params),
    placeholderData: keepPreviousData, // Smooth pagination
  });
}

/**
 * Hook to get stock KPIs
 * @param date - Optional date (YYYY-MM-DD) for historical KPIs
 */
export function useStockKPIs(date?: string | null) {
  return useQuery({
    queryKey: [...currentStockKeys.kpis(), date],
    queryFn: () => getStockKPIs(date || undefined),
    staleTime: 30_000, // 30s cache
  });
}

/**
 * Hook to get stock breakdown with aggregated movements by type
 * @see Sprint 27 - Stock a Date
 */
export function useStockBreakdown(
  rawProductId: string | null,
  params?: QueryStockBreakdownDto
) {
  return useQuery({
    queryKey: currentStockKeys.breakdown(rawProductId ?? '', params),
    queryFn: () => getStockBreakdown(rawProductId!, params),
    enabled: !!rawProductId,
  });
}
